/**
 * Every string of Add to Yaruta (OCR v1), verbatim from the YAR-556 COPY
 * table. Each line names its status there: Approved or Proposed. The bottom status toast strings live in
 * `lib/emailStatusRun.ts`; the Review Flow v2.0 Sources strings in
 * `reviewCopy.ts`.
 */

/** What the parent added: an image is a photo, a PDF is a file (§9). */
export type UploadKind = 'photo' | 'file';

/**
 * When the earlier copy was read, as the "already added" body names it
 * (UX kit §11): the phrase changes with the kind, not only the day.
 */
export type ReadOnDay =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'weekday' | 'date'; label: string }; // 'Monday' | 'Oct 26'

interface CopyPair {
  readonly headline: string;
  readonly body: string;
}

const KIND_NOUN: Readonly<Record<UploadKind, string>> = Object.freeze({
  photo: 'photo',
  file: 'file',
});

const KIND_LABEL: Readonly<Record<UploadKind, string>> = Object.freeze({
  photo: 'Photo',
  file: 'File',
});

export const ADD_TO_YARUTA_COPY = Object.freeze({
  // Approved (Kelvin, Oct 26)
  drawerButton: 'Add to Yaruta',
  // Approved (Kelvin, Oct 26)
  sheetTitle: 'Add to Yaruta',
  // Proposed
  sheetBody:
    "Yaruta reads it and finds the tasks and events. You review them before they're added.",
  // Proposed
  optionChoose: Object.freeze({
    label: 'Choose a photo or file',
    hint: 'Screenshots, images and PDFs',
  }),
  // Proposed (Oct 27)
  optionType: Object.freeze({
    label: 'Type it in',
    hint: 'Add a task or event yourself',
  }),
  // Approved (YAR-477)
  close: 'Close',
  refusal: Object.freeze({
    // Proposed
    unsupported_type: Object.freeze({
      headline: "Yaruta can't read this kind of file",
      body: 'Use a JPG, PNG or PDF',
    }),
    // Proposed (Oct 28)
    photo_too_large: Object.freeze({
      headline: 'This photo is too large',
      body: 'Use a photo under 25 MB',
    }),
    // Proposed (Oct 28)
    pdf_too_large: Object.freeze({
      headline: 'This file is too large',
      body: 'Use a PDF under 8 MB',
    }),
    // Proposed (Oct 28)
    pdf_too_long: Object.freeze({
      headline: 'This file is too long',
      body: 'Use a PDF with up to 30 pages',
    }),
  }) satisfies Readonly<Record<string, CopyPair>>,
  // Proposed
  alreadyAdded: (kind: UploadKind): string =>
    `This ${KIND_NOUN[kind]} was already added`,
  // Proposed ('on Monday' / 'on Oct 26'); 'today' and 'yesterday' are
  // Proposed (Kelvin) — "read it on today" was ungrammatical (review I-1)
  alreadyAddedBody: (day: ReadOnDay): string => {
    if (day.kind === 'today') return 'Yaruta read it today';
    if (day.kind === 'yesterday') return 'Yaruta read it yesterday';
    return `Yaruta read it on ${day.label}`;
  },
  // Proposed
  cardTitle: (kind: UploadKind, date: string): string =>
    `${KIND_LABEL[kind]} · ${date}`,
  // Proposed
  sourceKind: (kind: UploadKind): string => KIND_LABEL[kind],
  // Proposed
  addedByYou: 'Added by you',
  // Proposed
  sourceReferenceSubline: (kind: UploadKind): string =>
    `The ${KIND_NOUN[kind]} you added`,
  // Proposed (Nov 3). Drawn in Figma `.Review Card Content` Source=photo,
  // Status=reading.
  readingCardTitle: (kind: UploadKind): string => `Your ${KIND_NOUN[kind]}`,
  // Proposed (Nov 3). A PDF in View Source Reference.
  openFile: 'Open file',
  // Proposed (Nov 3). "Load", not "read": §9 keeps "read" for what Yaruta
  // does with an email.
  sourceLoadError: (kind: UploadKind): string =>
    `The ${KIND_NOUN[kind]} couldn't load`,
  // Proposed — not in YAR-556 COPY table, Kelvin to confirm. Screen-reader
  // name of the placeholder while the photo loads.
  sourceLoading: (kind: UploadKind): string =>
    `Loading the ${KIND_NOUN[kind]} you added`,
  // Proposed — Kelvin to confirm (not in the YAR-556 COPY table). The
  // Discard confirmation for an upload: never "email", never "Gmail" — the
  // parent's own file was never in Gmail.
  discardTitle: (kind: UploadKind): string =>
    `Discard this ${KIND_NOUN[kind]}?`,
  // Proposed — Kelvin to confirm. Review v0's toast after Discard.
  discardedToast: (kind: UploadKind): string => `${KIND_LABEL[kind]} discarded`,
  // Proposed — Kelvin to confirm. A couldn't-read upload has no items to lose.
  discardCouldntReadBody: "It won't show in Review anymore.",
  // Proposed — Kelvin to confirm. Review Items: the email body's first
  // sentence, without its Gmail reassurance.
  discardItemsBody: "The items won't be added to your schedule.",
  // Proposed (Nov 3)
  visionLimit: Object.freeze({
    headline: "Yaruta can't read more photos today",
    body: 'Try again tomorrow',
  }) satisfies CopyPair,
});
